import { useCallback, useState } from 'react';
import { ApiError } from '../lib/api-client';

/**
 * Result of triggering a scan — carries the poll key: the Step Functions
 * execution ARN once dispatched, or (while `QUEUED`, before an execution
 * exists) the `requestId` a queued scan message carries instead (AC-5).
 */
interface TriggerResult {
  executionArn?: string;
  requestId?: string;
}

interface UseRescanOptions {
  /**
   * When set, a 409 `SCAN_RECENTLY_COMPLETED` from the trigger is surfaced as this
   * informational notice instead of an error (the Feature page's 24h idempotency
   * gate). When omitted (the Repo page), all trigger failures become `rescanError`.
   */
  recentlyCompletedNotice?: string;
}

/**
 * Per-drill-down re-scan state machine, shared by RepoDrillDown and FeatureDrillDown.
 * Owns the execution ARN (persisted in sessionStorage so an in-flight scan survives a
 * reload), the in-flight/error/notice flags, and the on-complete reload. Callers guard
 * their own required params and pass a `trigger` closure (the delivery vs discovery API
 * call); the divergent bits stay out of the hook.
 */
export function useRescan(scanKey: string, opts: UseRescanOptions = {}) {
  const { recentlyCompletedNotice } = opts;

  const [rescanArn, setRescanArn] = useState<string | null>(() => {
    try { return sessionStorage.getItem(scanKey); } catch { return null; }
  });
  const [rescanning, setRescanning] = useState(() => rescanArn !== null);
  const [rescanError, setRescanError] = useState<string | null>(null);
  const [rescanNotice, setRescanNotice] = useState<string | null>(null);

  const start = useCallback(async (trigger: () => Promise<TriggerResult | undefined>) => {
    setRescanning(true);
    setRescanError(null);
    setRescanNotice(null);
    try {
      const result = await trigger();
      // Opaque poll key — an executionArn once dispatched, or the requestId while
      // the request sits QUEUED and no execution exists yet (AC-5). Both are
      // forwarded straight through to the fetcher; nothing downstream branches
      // on which one it got.
      const pollKey = result?.executionArn ?? result?.requestId;
      if (pollKey) {
        setRescanArn(pollKey);
        try { sessionStorage.setItem(scanKey, pollKey); } catch { /* noop */ }
      }
    } catch (err) {
      // 409 SCAN_RECENTLY_COMPLETED — surface as an informational notice only when
      // the caller opted in (Feature page); otherwise treat as a hard error (Repo page).
      if (
        recentlyCompletedNotice &&
        err instanceof ApiError &&
        (err.code === 'SCAN_RECENTLY_COMPLETED' || err.status === 409)
      ) {
        setRescanNotice(recentlyCompletedNotice);
      } else {
        setRescanError(err instanceof Error ? err.message : 'Failed to trigger rescan');
      }
      setRescanning(false);
    }
  }, [scanKey, recentlyCompletedNotice]);

  const handleRescanComplete = useCallback(() => {
    setRescanning(false);
    try { sessionStorage.removeItem(scanKey); } catch { /* noop */ }
    window.location.reload();
  }, [scanKey]);

  return { rescanArn, rescanning, rescanError, rescanNotice, start, handleRescanComplete };
}
